/**
 * The campaign monitoring overview's audio fan-out card: the sounds this campaign's tracked
 * accounts posted, and the accounts that reused each one. Figma has no frame for this card
 * (checked: the only audio node lives on "Operação · Operações · Coleta"), so it matches the
 * neighbouring monitoring cards' visual language rather than a drawn frame.
 */

import type { UseQueryResult } from '@tanstack/react-query';
import type { ReactNode } from 'react';

import { Absent } from '../../../chrome/measure';
import { PageSection } from '../../../chrome/page';
import { EmptyState, QueryStates } from '../../../chrome/state';
import { getLocale, type Locale } from '../../../i18n';
import { integer } from '../../../i18n/format';
import type { ApiFailureError } from '../../../lib/http/errors';
import { Badge } from '../../../ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../ui/table';
import type { AudioMatch, AudioSource, AudioTrack, DetectionMethodValue } from './api/schemas';
import { cm } from './messages';

function formatInstant(iso: string, locale: Locale): string {
  return new Intl.DateTimeFormat(locale, { dateStyle: 'short', timeStyle: 'short' }).format(
    new Date(iso),
  );
}

const MATCH_BASIS_KEY: Record<DetectionMethodValue, Parameters<typeof cm>[0]> = {
  FINGERPRINT_PHASH: 'audio.match.sameVideo',
  FINGERPRINT_ORB: 'audio.match.sameVideo',
  ACCOUNT_ID_MATCH: 'audio.match.sameAccount',
  HANDLE_MATCH: 'audio.match.sameSound',
  AUDIO_ID_MATCH: 'audio.match.sameSound',
};

function SourceItem(props: { source: AudioSource }): ReactNode {
  const { source } = props;
  const label = source.handle ?? source.post_id;
  return (
    <li>
      {source.url !== null ? (
        <a
          href={source.url}
          target="_blank"
          rel="noreferrer"
          className="text-primary-ink hover:underline"
        >
          {label}
        </a>
      ) : (
        label
      )}
    </li>
  );
}

function MatchItem(props: { match: AudioMatch; locale: Locale }): ReactNode {
  const { match, locale } = props;
  const when = match.occurred_at ?? match.observed_at;
  return (
    <li>
      {cm('audio.match.row', {
        account: match.amplifier_handle ?? match.amplifier_account_id,
        when: formatInstant(when, locale),
        basis: cm(MATCH_BASIS_KEY[match.method]),
      })}
    </li>
  );
}

function AudioRow(props: { track: AudioTrack; locale: Locale }): ReactNode {
  const { track, locale } = props;
  return (
    <TableRow>
      <TableCell className="whitespace-normal">
        <div className="flex items-center gap-2">
          <span>{track.title ?? cm('audio.untitled')}</span>
          {track.original && <Badge variant="secondary">{cm('audio.original')}</Badge>}
        </div>
      </TableCell>
      <TableCell>
        {track.last_lookup_at !== null ? (
          formatInstant(track.last_lookup_at, locale)
        ) : (
          <Absent reason={{ text: cm('audio.notLookedUpYet') }} />
        )}
      </TableCell>
      <TableCell>
        {track.last_reuse_count !== null ? (
          integer(track.last_reuse_count, locale)
        ) : (
          <Absent reason={{ text: cm('audio.notLookedUpYet') }} />
        )}
      </TableCell>
      <TableCell>
        {track.next_lookup_at !== null ? (
          formatInstant(track.next_lookup_at, locale)
        ) : (
          <Absent reason={{ text: cm('audio.noNextLookup') }} />
        )}
      </TableCell>
      <TableCell className="whitespace-normal">
        <ul className="flex flex-col gap-0.5">
          {track.sources.map((source) => (
            <SourceItem key={source.post_id} source={source} />
          ))}
        </ul>
      </TableCell>
      <TableCell className="whitespace-normal">
        {track.matches.length === 0 ? (
          <Absent reason={{ text: cm('audio.noReuse') }} />
        ) : (
          <ul className="flex flex-col gap-0.5">
            {track.matches.map((match) => (
              <MatchItem
                key={`${match.source_post_id}-${match.amplifier_account_id}-${match.observed_at}`}
                match={match}
                locale={locale}
              />
            ))}
          </ul>
        )}
      </TableCell>
    </TableRow>
  );
}

export function AudioCard(props: {
  campaignId: string;
  query: UseQueryResult<AudioTrack[], ApiFailureError>;
}): ReactNode {
  const locale = getLocale();
  return (
    <PageSection title={cm('audio.title')}>
      <QueryStates
        query={props.query}
        empty={
          <EmptyState
            title={cm('audio.empty.title')}
            description={cm('audio.empty.description')}
            tone="neutral"
          />
        }
      >
        {(tracks) => (
          <Table label={cm('audio.title')}>
            <TableHeader>
              <TableRow>
                <TableHead>{cm('audio.column.title')}</TableHead>
                <TableHead>{cm('audio.column.lastLookup')}</TableHead>
                <TableHead>{cm('audio.column.reuses')}</TableHead>
                <TableHead>{cm('audio.column.nextLookup')}</TableHead>
                <TableHead>{cm('audio.column.sources')}</TableHead>
                <TableHead>{cm('audio.column.matches')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {tracks.map((track) => (
                <AudioRow key={track.id} track={track} locale={locale} />
              ))}
            </TableBody>
          </Table>
        )}
      </QueryStates>
    </PageSection>
  );
}
